
"use client";

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { zenkakuToHankaku } from '@/lib/utils';

interface PinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: 'pos' | 'kds' | 'admin' | 'screen' | null;
  targetPath: string;
}

export function PinDialog({ open, onOpenChange, role, targetPath }: PinDialogProps) {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const router = useRouter();

  const handleSubmit = async () => {
    if (!role || pin.length < 4 || isLoading) return;
    setIsLoading(true);
    const success = await login({ role, pin });
    setIsLoading(false);

    if (success) {
      toast.success(`${role.toUpperCase()}としてログインしました。`);
      onOpenChange(false);
      router.push(targetPath);
      setPin('');
    } else {
      toast.error('PINコードが正しくありません。');
      setPin('');
    }
  };

  useEffect(() => {
    if (pin.length === 4) {
      void handleSubmit();
    }
  }, [pin, handleSubmit]);

  useEffect(() => {
    if (!open) {
      setPin('');
    }
  }, [open]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleFormSubmit}>
          <DialogHeader>
            <DialogTitle>{role?.toUpperCase()} 認証</DialogTitle>
            <DialogDescription>アクセスするには4桁のPINコードを入力してください。</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <InputOTP maxLength={4} value={pin} onChange={(value) => setPin(zenkakuToHankaku(value))}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <DialogFooter>
            <Button type="submit" className="w-full" disabled={isLoading || pin.length < 4}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              認証
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
